// App.js
import React, { useState, useMemo, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CacheProvider, ThemeProvider } from "@emotion/react";
import createCache from "@emotion/cache";
// import rtlPlugin from "stylis-plugin-rtl";
import PropTypes from "prop-types";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { CssBaseline, Icon, Box } from "@mui/material";
import routes from "routes";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import theme from "assets/theme";
import useAuthStore from "store/authStore";
import axios from "axios";
import themeDark from "assets/theme-dark";

import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import PaymentPopup from "payment";
import { BASE_URL } from "service";

// Protected Route Component with Payment Check
const ProtectedRoute = ({ children }) => {
  const { accessToken, email } = useAuthStore();
  const location = useLocation();

  // States
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [onClose, setOnClose] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(true);
  const [verifiedStatus, setVerifiedStatus] = useState("");

  const handleClose = () => {
    setOnClose(false);
  };

  if (!accessToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/subscription/${email}`);
        console.log(response);
        setIsSubscribed(response?.data?.isSubscribed);
        setVerifiedStatus(response?.data?.verified_status);
      } catch (error) {
        console.error("Error fetching subscription status:", error);
      }
    };

    fetchSubscriptionStatus();
  }, [paymentStatus]);

  // Show PaymentPopup only on dashboard routes if paymentStatus is false
  const isDashboardRoute = location.pathname.startsWith("/dashboard");

  return (
    <>
      {/* Show PaymentPopup if payment is not complete */}
      {!isSubscribed && isDashboardRoute && !onClose && (
        <PaymentPopup
          isSubscribed={isSubscribed}
          verifiedStatus={verifiedStatus}
          onClose={handleClose}
        />
      )}
      <div style={{ filter: !onClose && !isSubscribed ? "blur(10px)" : "none" }}>{children}</div>
    </>
  );
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const AuthRoute = ({ children }) => {
  const { accessToken } = useAuthStore();
  const location = useLocation();

  if (accessToken) {
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  return children;
};

AuthRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { accessToken, email } = useAuthStore();

  useEffect(() => {
    console.log(email);
  }, []);

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  useEffect(() => {
    const checkDate = () => {
      const stored = localStorage.getItem("visitedLinks");
      if (stored) {
        const { date } = JSON.parse(stored);
        const today = new Date().toDateString();
        if (date !== today) {
          //REdis
          // setVisitedLinks({});
          localStorage.setItem(
            "visitedLinks",
            JSON.stringify({
              date: today,
              links: {},
            })
          );
        }
      }
    };

    checkDate();
    // Iska reason ye hai ki agar user pura din tab khol kar rakhe to bhi next day aate hi automatic reset ho jaye, bina page refresh kare.
    const interval = setInterval(checkDate, 60000);
    return () => clearInterval(interval);
  }, []);

  // useMemo(() => {
  //   const cacheRtl = createCache({
  //     key: "rtl",
  //     stylisPlugins: [rtlPlugin],
  //   });
  //   setRtlCache(cacheRtl);
  // }, []);

  useEffect(() => {
    document.body.setAttribute("dir", controller.direction);
  }, [controller.direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !controller.openConfigurator);

  const configsButton = (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </Box>
  );

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        if (route.route.startsWith("/dashboard")) {
          return (
            <Route
              exact
              path={route.route}
              element={<ProtectedRoute>{route.component}</ProtectedRoute>}
              key={route.key}
            />
          );
        }

        if (["/login", "/register", "/"].includes(route.route)) {
          return (
            <Route
              exact
              path={route.route}
              element={<AuthRoute>{route.component}</AuthRoute>}
              key={route.key}
            />
          );
        }

        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const renderApp = () => (
    <>
      {accessToken && controller.layout === "dashboard" && (
        <>
          <Sidenav
            color={controller.sidenavColor}
            brand={
              (controller.transparentSidenav && !controller.darkMode) || controller.whiteSidenav
                ? brandDark
                : brandWhite
            }
            brandName="KamaiClub.com"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}

      <Routes>
        {getRoutes(routes)}
        <Route path="/" element={<Navigate to={accessToken ? "/dashboard" : "/login"} replace />} />
        <Route path="*" element={<Navigate to={accessToken ? "/dashboard" : "/login"} replace />} />
      </Routes>
    </>
  );

  return controller.direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={controller.darkMode ? themeDark : themeRTL}>
        <CssBaseline />
        {renderApp()}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={controller.darkMode ? themeDark : theme}>
      <CssBaseline />
      {renderApp()}
    </ThemeProvider>
  );
}
